// src/components/ui/WorkerHeader.js
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Building2, Activity, UserCircle, Download, Menu, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';

// 🌟 Universal Looms Extractor Helper (Prop + Multi-Key LocalStorage Support)
const getWorkerLoomsHeaderStr = (userData) => {
    let rawLoomsData = null;

    // 1. Check user prop
    if (userData) {
        rawLoomsData = userData.looms || userData.looms_assigned || userData.assigned_looms || userData.loom_ids || userData.loom || userData.looms_list;
    }

    // 2. Check localStorage multi-keys fallback
    if (!rawLoomsData) {
        const storageKeys = ['user', 'worker', 'workerData', 'loggedInUser', 'worker_user', 'auth_user'];
        for (const key of storageKeys) {
            try {
                const item = localStorage.getItem(key);
                if (item) {
                    const parsed = JSON.parse(item);
                    if (parsed) {
                        rawLoomsData = parsed.looms || parsed.looms_assigned || parsed.assigned_looms || parsed.loom_ids || parsed.loom || parsed.looms_list;
                        if (rawLoomsData) break;
                    }
                }
            } catch (e) {}
        }
    }

    if (!rawLoomsData) {
        rawLoomsData = localStorage.getItem('worker_looms') || localStorage.getItem('looms');
    }

    if (!rawLoomsData) return '';

    let loomIds = [];
    if (Array.isArray(rawLoomsData)) {
        loomIds = rawLoomsData.map(item => (typeof item === 'object' && item !== null) ? String(item.loom_id || item.id || '') : String(item));
    } else if (typeof rawLoomsData === 'string' || typeof rawLoomsData === 'number') {
        loomIds = String(rawLoomsData).split(',').map(s => s.trim());
    }

    const cleanLoomIds = loomIds.filter(id => id && id.trim() !== '' && id !== 'null' && id !== 'undefined');
    if (cleanLoomIds.length === 0) return '';

    const formattedLooms = cleanLoomIds.map(id => `L${String(id).trim().replace(/^L/i, '')}`).join(', ');
    return `${cleanLoomIds.length} LOOMS : ${formattedLooms}`;
};

const WorkerHeader = ({ 
    user,
    view, 
    lastView, 
    setView, 
    setShowSettingsMenu, 
    handleOpenProfile, 
    handleVoiceClick,
    isSearchOpen,
    setIsSearchOpen,
    onSearchClick
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false); 
    const menuRef = useRef(null);
    const [deepDiveData, setDeepDiveData] = useState(null); // 🌟 Date & Loom Info Store
const isDeepDive = !!deepDiveData;

useEffect(() => {
    const handleDeepDive = (e) => {
        if (typeof e.detail === 'object') {
            setDeepDiveData(e.detail);
        } else if (!e.detail) {
            setDeepDiveData(null);
        }
    };
    window.addEventListener('setDeepDiveState', handleDeepDive);
    return () => window.removeEventListener('setDeepDiveState', handleDeepDive);
}, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isMenuOpen]);

   const handleSearchToggle = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsMenuOpen(false);
        
        // 🌟 CUSTOM EVENT: WorkerHeader ka click WorkerHistory ko signal bhejega
        window.dispatchEvent(new CustomEvent('toggleWorkerSearch'));
        
        if (typeof setIsSearchOpen === 'function') {
            setIsSearchOpen(prev => !prev);
        } else if (typeof onSearchClick === 'function') {
            onSearchClick();
        }
    };

    return (
        /* 🌟 DARK BLUE PREMIUM MAIN TOP HEADER BAR 🌟 */
        <div style={{
            background: '#0F172A', 
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            padding: '14px 16px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            position: 'sticky', 
            top: 0, 
            zIndex: 100000, 
            minHeight: '64px',
            height: '68px',
            width: '100%',
            boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 20 }}>
        {view === 'profile' ? (
            <button 
                onClick={() => { window.speechSynthesis && window.speechSynthesis.cancel(); setView(lastView); }} 
                style={{
                    width: '32px', height: '32px', borderRadius: '10px',
                    background: '#1E293B', border: '1px solid rgba(255, 255, 255, 0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                }}
            >
                <ArrowLeft size={18} color="#FFFFFF" strokeWidth={2.5}/>
            </button>
        ) : isDeepDive ? (
            /* 🌟 SMART BACK BUTTON FOR LOOM DETAILS 🌟 */
            <button 
                onClick={() => { window.dispatchEvent(new CustomEvent('workerHistoryBack')); }}
                style={{
                    width: '34px', height: '34px', borderRadius: '10px',
                    background: '#1E293B', border: '1px solid rgba(255, 255, 255, 0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    cursor: 'pointer', padding: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', transition: 'all 0.2s'
                }}
            >
                <ArrowLeft size={18} color="#FFFFFF" strokeWidth={2.5} />
            </button>
        ) : (
            <div style={{ 
                width: '34px', height: '34px', borderRadius: '10px', 
                background: '#1E293B', border: '1px solid rgba(255, 255, 255, 0.15)', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
            }}>
                {view === 'history' ? (
                    <Activity size={18} color="#38BDF8" strokeWidth={2.5} />
                ) : (
                    <Building2 size={18} color="#38BDF8" strokeWidth={2.5} />
                )}
            </div>
        )}

        {/* 🌟 TITLE TEXT SIRF TAB DIKHEGA JAB LOOM DETAILS OPEN NA HO 🌟 */}
    {!isDeepDive && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <h1 style={{ fontSize: '14px', fontWeight: '900', color: '#FFFFFF', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase' }}>
                    {view === 'dashboard' ? 'MukadamPro' : view === 'history' ? 'My Detailed Production History' : view === 'vacancies' ? 'Find Jobs' : view === 'payments' ? 'Payments' : view === 'profile' ? 'My Profile' : 'Worker Portal'}
                </h1>
               <span style={{ fontSize: '9px', color: '#38BDF8', fontWeight: '800', letterSpacing: '0.4px', textTransform: 'uppercase', whiteSpace: 'normal', maxWidth: 'calc(100vw - 130px)', lineHeight: '1.4' }}>
            {view === 'dashboard' ? 'Worker Dashboard' : (view === 'history' ? getWorkerLoomsHeaderStr(user) : '')}
        </span>
            </div>
        )}
    </div>

    {/* 🌟 CENTERED PREV / NEXT DATE NAVIGATION (Loom Details khulne par 'Detailed Production History' ki jagah ye aayega) 🌟 */}
    {isDeepDive && deepDiveData && (
        <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', zIndex: 10 }}>
            <button 
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('workerHistoryPrevDay'))} 
                style={{ 
                    background: '#1E293B', border: '1px solid rgba(255,255,255,0.12)', 
                    borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', 
                    alignItems: 'center', justifyContent: 'center', 
                    opacity: deepDiveData.isFirst ? 0.3 : 1 
                }} 
                disabled={deepDiveData.isFirst}
            >
                <ChevronLeft size={18} color="#FFFFFF" strokeWidth={2.5} />
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', minWidth: '120px' }}>
                <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#FFFFFF', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
                    {deepDiveData.label}
                </h1>
                <span style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' }}>
                    {deepDiveData.subLabel}
                </span>
            </div>

            <button 
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('workerHistoryNextDay'))} 
                style={{ 
                    background: '#1E293B', border: '1px solid rgba(255,255,255,0.12)', 
                    borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', 
                    alignItems: 'center', justifyContent: 'center', 
                    opacity: deepDiveData.isLast ? 0.3 : 1 
                }} 
                disabled={deepDiveData.isLast}
            >
                <ChevronRight size={18} color="#FFFFFF" strokeWidth={2.5} />
            </button>
        </div>
    )}

           {/* Right Action Menu: SEARCH BUTTON + MENU BUTTON */}
            {view !== 'profile' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    
                    {/* 🌟 SEARCH BUTTON (SIRF WORKER HISTORY / VIEW === 'HISTORY' ME HI SHOW HOGA) 🌟 */}
                    {view === 'history' && (
                        <button 
                            type="button"
                            onClick={handleSearchToggle}
                            style={{ 
                                background: isSearchOpen ? 'rgba(56, 189, 248, 0.2)' : '#1E293B', 
                                borderRadius: '12px', 
                                border: isSearchOpen ? '1px solid #38BDF8' : '1px solid rgba(255,255,255,0.12)', 
                                cursor: 'pointer', 
                                padding: '8px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                transition: 'all 0.2s',
                                position: 'relative',
                                zIndex: 100001,
                                pointerEvents: 'auto'
                            }}
                        >
                            {isSearchOpen ? (
                                <X size={18} color="#38BDF8" strokeWidth={2.5} />
                            ) : (
                                <Search size={18} color="#FFFFFF" strokeWidth={2.5} />
                            )}
                        </button>
                    )}

                    {/* MENU BUTTON */}
                    <div ref={menuRef} style={{ position: 'relative' }}>
                        <button 
                            onClick={() => { setIsMenuOpen(!isMenuOpen); }} 
                            style={{
                                background: isMenuOpen ? '#334155' : '#1E293B', 
                                borderRadius: '12px', 
                                border: '1px solid rgba(255,255,255,0.12)', 
                                cursor: 'pointer', 
                                padding: '8px', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Menu size={18} color="#FFFFFF" strokeWidth={2.5} />
                        </button>

                        {isMenuOpen && (
                            <div style={{ 
                                position: 'absolute', 
                                top: '120%', 
                                right: 0, 
                                background: '#1E293B', 
                                borderRadius: '14px', 
                                boxShadow: '0 10px 30px rgba(0,0,0,0.5)', 
                                border: '1px solid rgba(255,255,255,0.12)', 
                                overflow: 'hidden', 
                                minWidth: '210px', 
                                zIndex: 1000, 
                                display: 'flex', 
                                flexDirection: 'column' 
                            }}>
                                {/* 1. Permanent Combined Option: My Profile & Settings */}
                                <div 
                                    onClick={(e) => { 
                                        e.stopPropagation();
                                        setIsMenuOpen(false); 
                                        if (handleVoiceClick) {
                                            handleVoiceClick(e, "प्रोफाइल और सेटिंग्स ओपन हो रही है।", handleOpenProfile, 'btn-profile');
                                        } else if (handleOpenProfile) {
                                            handleOpenProfile(); 
                                        } else if (setShowSettingsMenu) {
                                            setShowSettingsMenu(true);
                                        }
                                    }} 
                                    style={{ 
                                        display: 'flex', alignItems: 'center', gap: '10px', 
                                        padding: '14px 16px', cursor: 'pointer', 
                                        borderBottom: (view === 'payments' || view === 'history') ? '1px solid rgba(255,255,255,0.08)' : 'none', 
                                        background: '#1E293B' 
                                    }}
                                >
                                    <UserCircle size={16} color="#94A3B8" strokeWidth={2.5} style={{ flexShrink: 0 }} />
                                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
                                        My Profile & Settings
                                    </span>
                                </div>

                                {/* 2. Download Report Option */}
                                {(view === 'payments' || view === 'history') && (
                                    <div 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsMenuOpen(false);
                                            if (handleVoiceClick) {
                                                handleVoiceClick(e, "पीडीएफ रिपोर्ट डाउनलोड करने के ऑप्शन खुल रहे हैं।", null, 'header-pdf-trigger');
                                            }
                                            const hiddenBtn = document.getElementById('trigger-pdf-download');
                                            if (hiddenBtn) hiddenBtn.click();
                                        }} 
                                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', cursor: 'pointer', background: '#0F172A' }}
                                    >
                                        <Download size={16} color="#38BDF8" strokeWidth={2.5} style={{ flexShrink: 0 }} />
                                        <span style={{ fontSize: '13px', fontWeight: '800', color: '#38BDF8', whiteSpace: 'nowrap' }}>
                                            Download Report
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkerHeader;